import { supabase } from '../supabase';
import { resolveStageName, parsePipelineStages } from './opportunityAutomationContext';

export interface LinkableOpportunity {
  id: number;
  title: string;
}

/**
 * Finds the most recent open CRM opportunity for a customer that does not
 * already have a proposal linked.
 */
export async function findOpenLinkableOpportunityForCustomer(
  businessId: string,
  customerId: string,
): Promise<LinkableOpportunity | null> {
  if (!supabase || !businessId || !customerId) return null;

  const { data: opportunities, error } = await supabase
    .from('pipeline_opportunities')
    .select('id, title')
    .eq('business_id', businessId)
    .eq('customer_id', customerId)
    .eq('status', 'Open')
    .order('created_at', { ascending: false });

  if (error || !opportunities?.length) return null;

  const opportunityIds = opportunities.map((opp) => opp.id);
  const { data: linkedProposals, error: proposalError } = await supabase
    .from('proposals')
    .select('pipeline_opportunities_id')
    .eq('business_id', businessId)
    .neq('status', 'held')
    .in('pipeline_opportunities_id', opportunityIds);

  if (proposalError) {
    console.error('Error checking linked proposals for opportunities:', proposalError);
    return null;
  }

  const linkedOpportunityIds = new Set(
    (linkedProposals || [])
      .map((row) => row.pipeline_opportunities_id)
      .filter((id): id is number => id != null),
  );

  const linkable = opportunities.find((opp) => !linkedOpportunityIds.has(opp.id));
  return linkable ? { id: linkable.id, title: linkable.title } : null;
}

export interface OpenOpportunitySummary {
  id: number;
  title: string;
  value: number;
  pipeline_id: number;
  stage_id: number;
  pipeline_name: string;
  stage_name: string;
  /** All stages for this opportunity's pipeline, for inline stage switching. */
  stages: Array<{ id: number; name: string }>;
  source?: string | null;
  created_at: string;
  customer_id: string | null;
  next_follow_up_at: string | null;
}

export function resolveCustomerIdsForContact(
  customerId: string | undefined,
  contactInfo: string | undefined,
  customers: Array<{ id: string; phone?: string | null }>,
): string[] {
  const ids = new Set<string>();
  if (customerId) ids.add(customerId);

  const contactDigits = (contactInfo || '').replace(/\D/g, '');
  if (contactDigits.length >= 10) {
    for (const customer of customers) {
      if (!customer.phone) continue;
      const customerDigits = customer.phone.replace(/\D/g, '');
      if (
        customerDigits === contactDigits ||
        customerDigits === contactDigits.substring(1) ||
        `1${customerDigits}` === contactDigits
      ) {
        ids.add(customer.id);
      }
    }
  }

  return Array.from(ids);
}

/** Whether a single customer currently has at least one open CRM opportunity. */
export async function customerHasOpenOpportunity(
  businessId: string,
  customerId: string,
): Promise<boolean> {
  if (!supabase || !businessId || !customerId) return false;

  const { count, error } = await supabase
    .from('pipeline_opportunities')
    .select('id', { count: 'exact', head: true })
    .eq('business_id', businessId)
    .eq('customer_id', customerId)
    .eq('status', 'Open');

  if (error) {
    console.error('Error checking open opportunity for customer:', error);
    return false;
  }

  return (count ?? 0) > 0;
}

/** Customer IDs that currently have at least one open CRM opportunity. */
export async function getCustomerIdsWithOpenOpportunities(businessId: string): Promise<Set<string>> {
  if (!supabase || !businessId) return new Set();

  const { data, error } = await supabase
    .from('pipeline_opportunities')
    .select('customer_id')
    .eq('business_id', businessId)
    .eq('status', 'Open');

  if (error) {
    console.error('Error loading customers with open opportunities:', error);
    return new Set();
  }

  return new Set(
    (data || [])
      .map((row) => row.customer_id)
      .filter((id): id is string => !!id),
  );
}

// ── Instant Lead-banner cache ────────────────────────────────────────────────
// Open opportunities for a business are a small dataset. We fetch them ALL
// once (opportunities + pipelines in parallel), key them by customer, and the
// conversation Lead banner reads from memory — instant, like the message list.
// The Messages page re-primes on its pipeline_opportunities realtime events.

export const OPEN_OPPORTUNITIES_CACHE_EVENT = 'openOpportunitiesCacheUpdated';

const openOppsCache = new Map<
  string,
  { byCustomer: Map<string, OpenOpportunitySummary[]>; fetchedAt: number }
>();

/** Fetch every open opportunity for the business and cache them by customer. */
export async function primeOpenOpportunitiesCache(businessId: string): Promise<void> {
  if (!supabase || !businessId) return;
  try {
    const [oppsRes, pipesRes] = await Promise.all([
      supabase
        .from('pipeline_opportunities')
        .select('id, title, value, pipeline_id, stage_id, source, created_at, customer_id, next_follow_up_at')
        .eq('business_id', businessId)
        .eq('status', 'Open')
        .order('created_at', { ascending: false }),
      supabase.from('pipelines').select('id, pipeline_name, stages').eq('business_id', businessId),
    ]);
    if (oppsRes.error) throw oppsRes.error;

    const pipelineMap = new Map((pipesRes.data || []).map((pipeline) => [pipeline.id, pipeline]));
    const byCustomer = new Map<string, OpenOpportunitySummary[]>();
    for (const opp of oppsRes.data || []) {
      if (!opp.customer_id) continue;
      const pipeline = pipelineMap.get(opp.pipeline_id);
      const summary: OpenOpportunitySummary = {
        id: opp.id,
        title: opp.title,
        value: Number(opp.value) || 0,
        pipeline_id: opp.pipeline_id,
        stage_id: opp.stage_id,
        pipeline_name: pipeline?.pipeline_name || 'Pipeline',
        stage_name: resolveStageName(pipeline?.stages, opp.stage_id),
        stages: parsePipelineStages(pipeline?.stages).map((s) => ({ id: Number(s.id), name: s.name })),
        source: opp.source,
        created_at: opp.created_at,
        customer_id: opp.customer_id ?? null,
        next_follow_up_at: opp.next_follow_up_at ?? null,
      };
      const list = byCustomer.get(opp.customer_id) || [];
      list.push(summary);
      byCustomer.set(opp.customer_id, list);
    }

    openOppsCache.set(businessId, { byCustomer, fetchedAt: Date.now() });
    window.dispatchEvent(new CustomEvent(OPEN_OPPORTUNITIES_CACHE_EVENT, { detail: { businessId } }));
  } catch (error) {
    console.error('Error priming open-opportunities cache:', error);
  }
}

/** Synchronous cache read; null when the cache hasn't been primed yet. */
export function getCachedOpenOpportunitiesForCustomerIds(
  businessId: string,
  customerIds: string[],
): OpenOpportunitySummary[] | null {
  const entry = openOppsCache.get(businessId);
  if (!entry) return null;
  const seen = new Set<number>();
  const result: OpenOpportunitySummary[] = [];
  for (const id of customerIds) {
    for (const opp of entry.byCustomer.get(id) || []) {
      if (!seen.has(opp.id)) {
        seen.add(opp.id);
        result.push(opp);
      }
    }
  }
  return result;
}

export async function getOpenOpportunitiesForCustomerIds(
  businessId: string,
  customerIds: string[],
): Promise<OpenOpportunitySummary[]> {
  if (!supabase || !businessId || customerIds.length === 0) return [];

  const uniqueCustomerIds = [...new Set(customerIds)];
  const { data: opportunities, error } = await supabase
    .from('pipeline_opportunities')
    .select('id, title, value, pipeline_id, stage_id, source, created_at, customer_id, next_follow_up_at')
    .eq('business_id', businessId)
    .eq('status', 'Open')
    .in('customer_id', uniqueCustomerIds)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error loading open opportunities for customer:', error);
    return [];
  }

  if (!opportunities?.length) return [];

  const pipelineIds = [...new Set(opportunities.map((opp) => opp.pipeline_id))];
  const { data: pipelines, error: pipelineError } = await supabase
    .from('pipelines')
    .select('id, pipeline_name, stages')
    .eq('business_id', businessId)
    .in('id', pipelineIds);

  if (pipelineError) {
    console.error('Error loading pipelines for open opportunities:', pipelineError);
  }

  const pipelineMap = new Map((pipelines || []).map((pipeline) => [pipeline.id, pipeline]));

  return opportunities.map((opp) => {
    const pipeline = pipelineMap.get(opp.pipeline_id);
    return {
      id: opp.id,
      title: opp.title,
      value: Number(opp.value) || 0,
      pipeline_id: opp.pipeline_id,
      stage_id: opp.stage_id,
      pipeline_name: pipeline?.pipeline_name || 'Pipeline',
      stage_name: resolveStageName(pipeline?.stages, opp.stage_id),
      stages: parsePipelineStages(pipeline?.stages).map((s) => ({
        id: Number(s.id),
        name: s.name,
      })),
      source: opp.source,
      created_at: opp.created_at,
      customer_id: opp.customer_id ?? null,
      next_follow_up_at: opp.next_follow_up_at ?? null,
    };
  });
}
